import type {
  CreditOperation,
  CreditOperationConfig,
  KnownCreditOperation,
} from "@/lib/types/credit-guard.types";
import type { Provider, ProviderCategory } from "@/lib/types/provider.types";
import type { ServiceCreditPrice } from "@/store/api/pricingApi";

/**
 * The price list the credit guard reads, and how each operation is priced.
 *
 * Adding an operation is one entry here plus one `checkCredits` call at the
 * point of spend — no new plumbing.
 */
export const CREDIT_OPERATIONS: Record<
  KnownCreditOperation,
  CreditOperationConfig
> = {
  /** One row's "Show" on the email column — creates an email workflow. */
  "email-unlock": {
    label: "Email Unlock",
    providerCategories: ["email"],
    fallbackCost: 1,
  },
  /** One row's "Show" on the phone column — creates a phone workflow. */
  "phone-unlock": {
    label: "Phone Unlock",
    providerCategories: ["phone"],
    fallbackCost: 1,
  },
  /**
   * The bulk bar's "Show Details", which saves with `unlockType: "both"` and so
   * can draw on either waterfall.
   */
  "contact-unlock": {
    label: "Contact Unlock",
    providerCategories: ["email", "phone"],
    fallbackCost: 1,
  },
  /**
   * Priced by the credit service at 0 today ("Metered CSV export. Amount is a
   * placeholder"), so a check against it always passes. It is here so that the
   * day that price is set, gating export is a one-line `checkCredits` call
   * rather than a change to this file.
   */
  "csv-export": {
    label: "CSV Export",
    serviceType: "csvExport",
    fallbackCost: 0,
  },
};

export function getOperationConfig(
  operation: CreditOperation
): CreditOperationConfig | undefined {
  return CREDIT_OPERATIONS[operation as KnownCreditOperation];
}

export function getOperationLabel(operation: CreditOperation): string {
  const config = getOperationConfig(operation);
  if (config) return config.label;
  // An ad-hoc operation still gets readable wording: "email-unlock" -> "Email Unlock".
  return operation
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Which price sources an operation needs fetched before it can be priced —
 * so the guard fetches the provider catalogue for a waterfall and the pricing
 * bundle for a flat-rate action, rather than all of both for either.
 */
export function getOperationPriceSources(operation: CreditOperation): {
  categories: ProviderCategory[];
  needsServiceConfigs: boolean;
} {
  const config = getOperationConfig(operation);
  return {
    categories: config?.providerCategories ?? [],
    needsServiceConfigs: !!config?.serviceType,
  };
}

/**
 * The cheapest attempt in a waterfall that actually costs something.
 *
 * Deliberately the smallest POSITIVE cost, not the smallest cost. The phone
 * catalogue currently carries a free provider (enrichlayer, creditCost 0), and
 * a plain minimum would make every phone unlock cost "0" — the guard would
 * wave through a user with an empty balance, who would then watch the
 * waterfall fall through to the first provider that does charge and fail
 * there. The smallest positive cost is the least credit that lets any paid
 * attempt in the chain go ahead.
 *
 * Returns undefined when there is nothing to read (no catalogue yet), so the
 * caller can fall back rather than treat "unknown" as "free".
 */
export function minimumProviderCost(
  providers: Provider[] | undefined
): number | undefined {
  if (!providers?.length) return undefined;
  const positive = providers
    .filter((provider) => provider.isActive && provider.creditCost > 0)
    .map((provider) => provider.creditCost);
  // Every active provider is free: genuinely 0, not unknown.
  if (!positive.length) return providers.some((p) => p.isActive) ? 0 : undefined;
  return Math.min(...positive);
}

/** The `serviceConfigs` entry for a flat-rate operation, by its `type`. */
export function serviceCost(
  serviceConfigs: ServiceCreditPrice[] | undefined,
  serviceType: string
): number | undefined {
  return serviceConfigs?.find((config) => config.type === serviceType)?.amount;
}

/**
 * What one item of `operation` costs, given whatever price data is to hand.
 *
 * `providersByCategory` is keyed by category and may be partially populated —
 * an operation spanning email and phone takes the cheaper of the two it can
 * actually see. Falls back to the registry's `fallbackCost` rather than 0, so a
 * missing price list blocks an empty balance instead of waving it through.
 */
export function resolveOperationCost(
  operation: CreditOperation,
  {
    providersByCategory,
    serviceConfigs,
  }: {
    providersByCategory?: Partial<Record<string, Provider[] | undefined>>;
    serviceConfigs?: ServiceCreditPrice[];
  }
): number {
  const config = getOperationConfig(operation);
  if (!config) return 0;

  if (config.serviceType) {
    return serviceCost(serviceConfigs, config.serviceType) ?? config.fallbackCost;
  }

  if (config.providerCategories?.length) {
    const costs = config.providerCategories
      .map((category) => minimumProviderCost(providersByCategory?.[category]))
      .filter((cost): cost is number => cost !== undefined);
    if (!costs.length) return config.fallbackCost;
    return Math.min(...costs);
  }

  return config.fallbackCost;
}

/**
 * What one item of `operation` actually SPENDS, as opposed to the least it
 * needs to begin (resolveOperationCost).
 *
 * The two differ only for an operation spanning more than one category.
 * "contact-unlock" runs BOTH waterfalls per contact — a combined bulk run
 * creates one email workflow and one phone workflow for every contact — so its
 * spend is the sum of the categories, while the minimum to START is still the
 * cheapest single attempt. Pricing the job off the minimum understated a
 * combined run by the whole phone leg.
 */
export function resolveOperationSpend(
  operation: CreditOperation,
  {
    providersByCategory,
    serviceConfigs,
  }: {
    providersByCategory?: Partial<Record<string, Provider[] | undefined>>;
    serviceConfigs?: ServiceCreditPrice[];
  }
): number {
  const config = getOperationConfig(operation);
  if (!config) return 0;

  if (config.providerCategories && config.providerCategories.length > 1) {
    // Each category falls back INDEPENDENTLY, so a partly-loaded catalogue
    // still prices every leg that will actually run. Summing only the
    // categories that answered meant a failed phone fetch quoted a combined
    // unlock at the email price alone - half the real spend, in the one
    // direction a credit warning must never err.
    //
    // `??` not `||`: minimumProviderCost returns a genuine 0 when every active
    // provider in a category is free, and that 0 must survive.
    return config.providerCategories.reduce((total, category) => {
      const cost = minimumProviderCost(providersByCategory?.[category]);
      return total + (cost ?? config.fallbackCost);
    }, 0);
  }

  return resolveOperationCost(operation, { providersByCategory, serviceConfigs });
}
